import React from "react";
import * as Iconoir from "iconoir-react";

// Simple in-memory cache to avoid recomputing lookups.
const iconCache: Record<string, React.ComponentType<any>> = {};

function toPascalCase(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
}

function resolveIcon(name: string): React.ComponentType<any> | null {
  if (!name) return null;
  // Return cached component if present.
  if (iconCache[name]) return iconCache[name];

  // Attempt direct export key (user may already pass PascalCase)
  const direct = (Iconoir as any)[name];
  if (direct) {
    iconCache[name] = direct;
    return direct;
  }

  // Convert kebab-case to PascalCase and attempt lookup.
  const pascal = toPascalCase(name);
  const comp = (Iconoir as any)[pascal];
  if (comp) {
    iconCache[name] = comp; // cache under original provided name
    return comp;
  }

  // Not found.
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(
      `[IconoirRender] Unknown icon name "${name}". Provide a valid iconoir slug or PascalCase export name. Falling back.`
    );
  }
  return null;
}

export interface IconoirRenderProps {
  name?: string | null;
  color?: string | null;
  size?: number;
  strokeWidth?: number;
  className?: string;
  fallback?: React.ReactNode;
}

export const IconoirRender: React.FC<IconoirRenderProps> = ({
  name,
  color,
  size = 32,
  strokeWidth = 1.6,
  className,
  fallback = null,
}) => {
  if (!name) return fallback;
  const Comp = resolveIcon(name);
  if (!Comp) return fallback;
  return (
    <Comp
      color={color || "currentColor"}
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      className={className}
    />
  );
};
